import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import api, { API_BASE_URL } from "../../src/utils/api";
import EditPropertyModal from "../components/EditPropertyModal";

const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error("Failed to fetch properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleView = (account_id) => {
    navigate(`/home/clubmembers/${account_id}?from=properties`);
  };

  const handleEdit = (property) => {
    setSelectedPropertyId(property.id);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedPropertyId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;
    try {
      await api.delete(`/properties/${id}`);
      setProperties((prev) => prev.filter((prop) => prop.id !== id));
      alert("Property deleted successfully!");
    } catch (error) {
      console.error("Failed to delete property:", error);
    }
  };

  const filteredProperties = properties.filter((property) =>
    [
      property.street_1,
      property.city,
      property.state,
      property.postal_code,
      property.mailing_name,
      property.emails,
      property.neighborhood,
    ]
      .map((val) => val?.toLowerCase() || "")
      .some((val) => val.includes(searchQuery))
  );

  const columns = [
    { field: "account_id", headerName: "Club Member Code", flex: 1 },
    { field: "street_1", headerName: "Street", flex: 1.5 },
    { field: "city", headerName: "City", flex: 1 },
    { field: "state", headerName: "State", flex: 0.5 },
    { field: "postal_code", headerName: "ZIP", flex: 0.7 },
    { field: "mailing_name", headerName: "Mailing Name", flex: 1.2 },
    { field: "emails", headerName: "Email", flex: 1.3 },
    { field: "phone_numbers", headerName: "Phone", flex: 1 },
    { field: "neighborhood", headerName: "Neighborhood", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton color="primary" onClick={() => handleView(params.row.account_id)} size="small">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton color="warning" onClick={() => handleEdit(params.row)} size="small">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton color="error" onClick={() => handleDelete(params.row.id)} size="small">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        🏡 Properties
      </Typography>

      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <TextField
          label="Search Properties"
          variant="outlined"
          onChange={handleSearch}
          sx={{ width: 300 }}
          InputProps={{
            endAdornment: <SearchIcon />,
          }}
        />
        <Typography>Total Records: {filteredProperties.length}</Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Box sx={{ minWidth: "1200px" }}>
            <DataGrid
              rows={filteredProperties}
              columns={columns}
              getRowId={(row) => row.id}
              pageSize={100}
              autoHeight
              disableSelectionOnClick
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                  color: '#000',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-cell': {
                  color: '#333',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f9f9f9',
                },
              }}
            />
          </Box>
        </Box>
      )}

      {modalOpen && (
        <EditPropertyModal
          open={modalOpen}
          handleClose={handleCloseModal}
          propertyId={selectedPropertyId}
          onSave={fetchProperties}
        />
      )}
    </Box>
  );
};

export default PropertiesPage;
